import * as Sentry from '@sentry/react';
import { posthog } from 'posthog-js';

import { CHUNK_LOAD_ERROR_PATTERNS } from '../app-version/chunk-load-error.js';
import { attachConsoleOrigin } from './console-log-origin.js';

type SentryEnvironment = 'development' | 'ci' | 'preview' | 'staging' | 'production';

export type InitSentryOptions = {
  environment: SentryEnvironment;
  dsn: string | undefined;
  release: string | undefined;
  // Host port the `spotlight` Docker service publishes — and its listen port
  // too; the two match (the overlay UI connects to the port the sidecar
  // advertises). The +8 slot in eco-platform's 8200-block. Driven by
  // VITE_SPOTLIGHT_PORT (default 8208); a `-dev` worktree uses 8308.
  spotlightPort: number;
  // The eco-platform backend origin (VITE_API_URL). Used as
  // browserTracingIntegration's tracePropagationTargets so the sentry-trace /
  // baggage headers ride only on calls to our own API — connecting frontend
  // and backend into one distributed trace — and not on eco-tools or
  // third-party requests.
  apiUrl: string;
};

// Per-environment trace sampling. Tracing is necessary-tier alongside errors:
// no cookies / device storage, and `sendDefaultPii: false`, so it carries no
// consent trigger (see docs/features/observability/design.md §Frontend). Full
// on staging + preview for validation; production sampled to bound volume.
const TRACES_DEFAULTS: Record<SentryEnvironment, number> = {
  development: 1.0,
  ci: 0.1,
  preview: 1.0,
  staging: 1.0,
  production: 0.1,
};

/**
 * Initialise the Sentry browser SDK.
 *
 * - `development` → no DSN; events go only to the local Spotlight sidecar
 *   (the `spotlight` service in `apps/platform-backend/docker-compose.yml`; overlay at
 *   `localhost:<spotlightPort>`).
 * - `ci | staging | production` → a DSN must be set, Spotlight is omitted,
 *   events go to Sentry SaaS.
 * - No DSN outside `development` → no-op (e.g. a build that didn't get a DSN).
 *
 * Tracing: `browserTracingIntegration` produces page-load / navigation
 * transactions + Web Vitals, sampled per environment (`TRACES_DEFAULTS`), and
 * propagates trace headers to `apiUrl` for frontend↔backend distributed traces.
 * Necessary-tier (no cookies / PII); see design.md §Frontend integration.
 *
 * Logs: `console.warn` / `console.error` are forwarded as Sentry logs
 * (`enableLogs` + `consoleLoggingIntegration`) — useful because the browser
 * console is invisible in prod. `console.log`/`console.info`/`console.debug`
 * are NOT forwarded (too noisy); the backend uses Pino → Cloud Logging and
 * doesn't pipe into Sentry logs. A `beforeSendLog` hook (`attachConsoleOrigin`)
 * tags each forwarded log with its originating stack so a noisy source can be
 * filtered specifically in Sentry.
 *
 * PII: `sendDefaultPii: false` keeps the SDK conservative (no cookies, request
 * bodies, IPs, or `Authorization` headers) and Sentry's server-side data
 * scrubbers redact `password`/`token`/etc. before storage — no custom
 * `beforeSend` scrubber. See docs/features/observability/design.md.
 */
export function initSentry(opts: InitSentryOptions): void {
  const useSpotlight = opts.environment === 'development';
  if (!useSpotlight && !opts.dsn) return;

  Sentry.init({
    dsn: opts.dsn,
    environment: opts.environment,
    release: opts.release,
    enableLogs: true,
    integrations: [
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Not a default integration (the SDK ships no tracing instrumentation to
      // save bundle size), so it must be listed explicitly. Produces page-load
      // + navigation transactions, Web Vitals, and fetch/XHR spans. (Header
      // propagation is the top-level `tracePropagationTargets` below.)
      Sentry.browserTracingIntegration(),
      ...(useSpotlight
        ? [
            Sentry.spotlightBrowserIntegration({
              sidecarUrl: `http://localhost:${opts.spotlightPort}/stream`,
            }),
          ]
        : []),
    ],
    tracesSampleRate: TRACES_DEFAULTS[opts.environment],
    // Attach sentry-trace / baggage headers only to our own backend (→
    // frontend↔backend distributed traces), never eco-tools or third parties.
    tracePropagationTargets: [opts.apiUrl],
    sendDefaultPii: false,
    // A stale-chunk fetch failure after a deploy is not an application crash;
    // chunk-load recovery logs it (warn on recover, error when it can't).
    // Keep it out of Issues — see docs/features/chunk-load-recovery/design.md.
    ignoreErrors: [...CHUNK_LOAD_ERROR_PATTERNS],
    beforeSend: attachPostHogContext,
    // Attach the originating stack to console-forwarded logs so warnings can be
    // filtered by source in Sentry (the integration captures no stack itself).
    beforeSendLog: attachConsoleOrigin,
  });
}

/**
 * Attach the active PostHog `distinct_id` + `session_id` to the Sentry
 * event so the issue links straight to the matching session replay.
 * Reads the bundled posthog-js singleton directly — no `window` lookup,
 * no separate CDN load. Pre-init, the methods return undefined and the
 * hook passes the event through untouched; post-init (after `initPostHog`
 * + opt-in), they return real ids and the tags get attached.
 *
 * `@sentry/react` has no first-party PostHog integration, so we do this
 * inline as a `beforeSend` hook.
 */
function attachPostHogContext<E extends { tags?: Record<string, unknown> }>(event: E): E {
  let distinctId: string | undefined;
  let sessionId: string | undefined;
  try {
    distinctId = posthog.get_distinct_id();
    sessionId = posthog.get_session_id();
  } catch {
    return event;
  }
  if (!distinctId && !sessionId) return event;
  return {
    ...event,
    tags: {
      ...event.tags,
      ...(distinctId ? { posthog_distinct_id: distinctId } : {}),
      ...(sessionId ? { posthog_session_id: sessionId } : {}),
    },
  };
}

/**
 * Set or clear the Sentry user context.
 *
 * The id is the noma platform `user_id` (UUID from `/me`) — opaque and
 * not PII. Fires unconditionally once `/me` resolves (Sentry errors are
 * classified `necessary` per consent-management/design.md). Pass `null`
 * on sign-out.
 */
export function setSentryUser(id: string | null): void {
  Sentry.setUser(id === null ? null : { id });
}
